// =============================================================================
// SimSense Platform — Run Averages Page
// =============================================================================

import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { formatLapTime, formatLapTimeMs } from "../utils/formatters";
import { api } from "../utils/api";
import { useToast } from "../components/Toast";
import { useUnits } from "../hooks/useUnits";
import { Select } from "../components/Select";

export function RunAverages() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const units = useUnits();
  const toast = useToast();

  const [sessionA, setSessionA] = useState<any>(null);
  const [manifestA, setManifestA] = useState<any>(null);
  
  const [sessionBId, setSessionBId] = useState<string>(id!);
  const [, setSessionB] = useState<any>(null);
  const [manifestB, setManifestB] = useState<any>(null);
  
  const [allSessions, setAllSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMemberSearch, setShowMemberSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const stintAIndex = Number(searchParams.get("stintA") || 0);
  const stintBIndex = Number(searchParams.get("stintB") || 1);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  // Initial Load: Session A, Manifest A, and All Sessions list
  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        const [sData, mData, list] = await Promise.all([
          api<any>(`/api/sessions/${id}`),
          api<any>(`/api/sessions/${id}/lap-manifest`),
          api<any[]>("/api/sessions"),
        ]);
        setSessionA(sData);
        setManifestA(mData);
        setSessionB(sData);
        document.title = `Run Averages — ${sData.trackName || "Session"} | SimSense`;
        setManifestB(mData);
        
        // Filter sessions by same track/car for cleaner dropdown [Cody Request]
        const filtered = list.filter((s: any) => 
          s.trackName === sData.trackName && s.carName === sData.carName
        );
        setAllSessions(filtered);
        
        // Set default indices if not in URL
        if (!searchParams.get("stintA") && mData.stints.length >= 2) {
          setSearchParams({ stintA: "0", stintB: "1" });
        }
      } catch (err) {
        console.error("Failed to fetch initial data", err);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [id]);

  const handleMemberSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.length < 2) return;
    try {
      const users = await api<any[]>(`/api/profiles/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(users);
    } catch (err) {
      console.error("Search failed", err);
    }
  };

  const selectComparisonMember = async (userId: string) => {
    try {
      // Fetch this user's sessions for the same track/car
      const sessions = await api<any[]>(`/api/profiles/${userId}/sessions?track_name=${encodeURIComponent(sessionA.trackName)}&car_name=${encodeURIComponent(sessionA.carName)}`);
      if (sessions.length === 0) {
        toast.info("No matching sessions found for this user on this track/car combo.");
        return;
      }
      setAllSessions(sessions);
      setSessionBId(sessions[0].id);
      setShowMemberSearch(false);
    } catch (err) {
      toast.error("Failed to fetch member sessions.");
    }
  };

  // Load Session B / Manifest B when ID changes
  useEffect(() => {
    if (sessionBId === id) return; // Already loaded via init
    
    async function fetchB() {
      try {
        const [sData, mData] = await Promise.all([
          api<any>(`/api/sessions/${sessionBId}`),
          api<any>(`/api/sessions/${sessionBId}/lap-manifest`),
        ]);
        setSessionB(sData);
        setManifestB(mData);
      } catch (err) {
        console.error("Failed to fetch session B", err);
      }
    }
    fetchB();
  }, [sessionBId, id]);

  if (loading || !sessionA || !manifestA) return <div style={{ padding: "40px", textAlign: "center" }}>Loading run data...</div>;

  const stintA = manifestA.stints.find((s: any) => s.stintIndex === stintAIndex);
  const stintB = manifestB?.stints?.find((s: any) => s.stintIndex === stintBIndex);

  const lapsA = manifestA.laps.filter((l: any) => l.stintIndex === stintAIndex);
  const lapsB = manifestB?.laps?.filter((l: any) => l.stintIndex === stintBIndex) || [];

  const hasComparison = (sessionBId !== id) || (stintBIndex !== stintAIndex);

  return (
    <div>
      {/* Breadcrumbs */}
      <div style={{ marginBottom: "16px", fontSize: "13px", color: "var(--text2)" }}>
        <Link to="/" style={{ color: "var(--accent)", textDecoration: "none" }}>Sessions</Link>
        {" / "}
        <Link to={`/session/${id}`} style={{ color: "var(--accent)", textDecoration: "none" }}>{sessionA.trackName}</Link>
        {" / "}
        <span>Run Averages</span>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h2>Run Averages</h2>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          
          {/* Comparison Controls */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "8px 12px", borderRadius: "8px", border: "1px solid var(--border)" }}>
            <span style={{ fontSize: "12px", color: "var(--text2)", fontWeight: 600 }}>RUN A:</span>
            <Select
              value={String(stintAIndex)}
              onChange={v => setSearchParams({ stintA: v, stintB: String(stintBIndex) })}
              options={manifestA.stints.map((s: any) => ({ value: String(s.stintIndex), label: `Run ${s.stintIndex + 1} (${s.setupName || "Base"})` }))}
              size="sm"
            />

            <span style={{ color: "var(--text2)", margin: "0 4px" }}>vs</span>

            <span style={{ fontSize: "12px", color: "var(--text2)", fontWeight: 600 }}>RUN B:</span>
            <Select
              value={sessionBId}
              onChange={v => setSessionBId(v)}
              options={[
                { value: id!, label: "Current Session" },
                ...allSessions.filter(s => s.id !== id).map(s => ({ value: s.id, label: `${s.trackName} (${new Date(s.sessionDate).toLocaleDateString()})` }))
              ]}
              size="sm"
              style={{ maxWidth: "180px" }}
            />
            <Select
              value={String(stintBIndex)}
              onChange={v => setSearchParams({ stintA: String(stintAIndex), stintB: v })}
              options={(manifestB?.stints || []).map((s: any) => ({ value: String(s.stintIndex), label: `Run ${s.stintIndex + 1} (${s.setupName || "Base"})` }))}
              size="sm"
            />

            <button 
              onClick={() => setShowMemberSearch(true)}
              style={{ marginLeft: "8px", background: "none", border: "1px solid var(--border)", color: "var(--accent)", padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "11px", fontWeight: 600 }}
            >
              🔍 SEARCH MEMBER
            </button>
          </div>

        </div>
      </div>

      {/* Member Search Modal */}
      {showMemberSearch && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: "var(--bg2)", width: "100%", maxWidth: "500px", borderRadius: "12px", border: "1px solid var(--border)", padding: "24px", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
              <h3>Search Member for Comparison</h3>
              <button onClick={() => setShowMemberSearch(false)} style={{ background: "none", border: "none", color: "var(--text2)", cursor: "pointer", fontSize: "20px" }}>×</button>
            </div>
            
            <form onSubmit={handleMemberSearch} style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
              <input 
                autoFocus
                type="text" 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by display name or email..."
                style={{ flex: 1, padding: "10px", background: "rgba(0,0,0,0.2)", border: "1px solid var(--border)", borderRadius: "4px", color: "#fff" }}
              />
              <button type="submit" style={{ padding: "10px 20px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "4px", fontWeight: 600, cursor: "pointer" }}>Search</button>
            </form>

            <div style={{ maxHeight: "300px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
              {searchResults.length === 0 && searchQuery.length > 0 && <div style={{ textAlign: "center", padding: "20px", color: "var(--text3)" }}>No public profiles found.</div>}
              {searchResults.map(user => (
                <div 
                  key={user.id} 
                  onClick={() => selectComparisonMember(user.id)}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "rgba(255,255,255,0.03)", borderRadius: "6px", cursor: "pointer", border: "1px solid transparent" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>{user.displayName}</div>
                    <div style={{ fontSize: "11px", color: "var(--text3)" }}>{user.sessionCount} sessions shared</div>
                  </div>
                  <span style={{ fontSize: "11px", color: "var(--accent)", fontWeight: 600 }}>SELECT →</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: hasComparison ? "1fr 1fr" : "1fr", gap: "24px" }}>
        <StintSummaryCard label={`Run ${stintAIndex + 1}`} stint={stintA} color="#3b82f6" />
        {hasComparison && <StintSummaryCard label={`Run ${stintBIndex + 1}`} stint={stintB} color="#ef4444" />}
      </div>

      {/* OT-252: Total Time Split Comparison */}
      {hasComparison && lapsA.length > 0 && lapsB.length > 0 && (
        <TotalTimeSplits
          lapsA={lapsA}
          lapsB={lapsB}
          labelA={`Run ${stintAIndex + 1}`}
          labelB={`Run ${stintBIndex + 1}`}
        />
      )}

      <div style={{ marginTop: "32px", background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
        <h3 style={{ fontSize: "16px", marginBottom: "24px" }}>Lap Time Comparison</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", color: "var(--text2)" }}>
                <th style={{ padding: "12px 8px" }}>Ref. Lap</th>
                <th style={{ padding: "12px 8px" }}>Run {stintAIndex + 1}</th>
                {hasComparison && <th style={{ padding: "12px 8px" }}>Run {stintBIndex + 1}</th>}
                {hasComparison && <th style={{ padding: "12px 8px" }}>Delta</th>}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.max(lapsA.length, lapsB.length) }).map((_, i) => {
                const lapA = lapsA[i];
                const lapB = lapsB[i];
                const delta = (lapA?.lapTime && lapB?.lapTime) ? lapA.lapTime - lapB.lapTime : null;
                
                return (
                  <tr
                    key={i}
                    onClick={() => setSelectedRow(i === selectedRow ? null : i)}
                    style={{
                      borderBottom: "1px solid var(--border)",
                      cursor: "pointer",
                      background: i === selectedRow ? "rgba(59, 130, 246, 0.1)" : undefined,
                      transition: "background 0.15s ease"
                    }}
                  >
                    <td style={{ padding: "8px 8px", color: "var(--text2)" }}>{i + 1}</td>
                    <td style={{ padding: "8px 8px", fontVariantNumeric: "tabular-nums" }}>{formatLapTime(lapA?.lapTime)}</td>
                    {hasComparison && (
                      <td style={{ padding: "8px 8px", fontVariantNumeric: "tabular-nums" }}>
                        {formatLapTime(lapB?.lapTime)}
                      </td>
                    )}
                    {hasComparison && (
                      <td style={{ padding: "8px 8px", fontVariantNumeric: "tabular-nums", color: getDeltaColor(delta) }}>
                        {delta !== null ? units.formatDelta(delta / 1000) : "-"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StintSummaryCard({ label, stint, color }: { label: string, stint: any, color: string }) {
  const units = useUnits();
  if (!stint) return null;
  const s = stint.summary;

  return (
    <div style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)", borderTop: `4px solid ${color}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
        <div>
          <h3 style={{ fontSize: "18px", marginBottom: "4px" }}>{label}</h3>
          <div style={{ fontSize: "13px", color: "var(--accent)", fontWeight: 600 }}>{stint.setupName || "Unknown Setup"}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "12px", color: "var(--text2)" }}>Laps {stint.startLap} – {stint.endLap}</div>
          {stint.isCommercial && (
            <div title={stint.commercialBrand ? `Official setup by ${stint.commercialBrand}` : "Commercial setup"} style={{
              fontSize: "10px", background: "rgba(245, 158, 11, 0.12)", color: "#fbbf24",
              padding: "2px 8px", borderRadius: "4px", fontWeight: 600, display: "inline-flex",
              alignItems: "center", gap: "4px", marginTop: "4px", border: "1px solid rgba(245, 158, 11, 0.25)",
            }}>
              <span style={{ fontSize: "10px" }}>&#x2606;</span> {stint.commercialBrand ? `Official ${stint.commercialBrand} Setup` : "Commercial Setup"}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <Metric label="Average Lap" value={formatLapTimeMs(s?.avgLapMs)} />
        <Metric label="Best Lap" value={formatLapTimeMs(s?.bestLapMs)} />
        <Metric 
          label="Consistency" 
          value={`±${units.formatDelta((s?.stddevMs || 0) / 1000).replace('s', '')}s`} 
          tooltip="Average variation between lap times. Lower is better."
        />
        <Metric
          label="Pace Falloff"
          value={`${units.formatDelta((s?.falloffSlopeMsPerLap || 0) / 1000).replace('s', '')}s / lap`}
          tooltip="Average time lost per lap due to tire wear or fuel weight."
        />
      </div>
    </div>
  );
}

function Metric({ label, value, tooltip }: { label: string, value: string, tooltip?: string }) {
  return (
    <div title={tooltip} style={{ cursor: tooltip ? "help" : "default" }}>
      <div style={{ fontSize: "12px", color: "var(--text2)", textTransform: "uppercase", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "20px", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

function getDeltaColor(delta: number | null): string {
  if (delta === null || Math.abs(delta) < 1) return "inherit";
  return delta > 0 ? "#f87171" : "#4ade80"; // Positive delta = slower (red)
}

function TotalTimeSplits({ lapsA, lapsB, labelA, labelB }: {
  lapsA: any[];
  lapsB: any[];
  labelA: string;
  labelB: string;
}) {
  const units = useUnits();

  // Build cumulative totals
  const sharedLaps = Math.min(lapsA.length, lapsB.length);
  const maxLaps = Math.max(lapsA.length, lapsB.length);

  // Dynamic checkpoints based on run length
  const FIXED_CHECKPOINTS = [5, 10, 15, 20, 25, 30, 40, 50, 60];
  const checkpoints = FIXED_CHECKPOINTS.filter(cp => cp <= sharedLaps);
  // Always include the final shared lap count
  if (sharedLaps > 0 && !checkpoints.includes(sharedLaps)) {
    checkpoints.push(sharedLaps);
  }

  if (checkpoints.length === 0) return null;

  const cumA = (n: number) => lapsA.slice(0, n).reduce((s, l) => s + (l.lapTime || 0), 0);
  const cumB = (n: number) => lapsB.slice(0, n).reduce((s, l) => s + (l.lapTime || 0), 0);

  return (
    <div style={{ marginTop: "24px", background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
      <h3 style={{ fontSize: "16px", marginBottom: "16px" }}>Total Time Comparison</h3>
      {lapsA.length !== lapsB.length && (
        <div style={{ fontSize: "11px", color: "var(--text3)", marginBottom: "12px" }}>
          Runs have different lengths ({lapsA.length} vs {lapsB.length} laps). Comparing shared laps only up to each checkpoint.
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", color: "var(--text2)" }}>
              <th style={{ padding: "10px 8px" }}>Checkpoint</th>
              <th style={{ padding: "10px 8px" }}>{labelA} Total</th>
              <th style={{ padding: "10px 8px" }}>{labelB} Total</th>
              <th style={{ padding: "10px 8px" }}>Delta</th>
              <th style={{ padding: "10px 8px" }}>Result</th>
            </tr>
          </thead>
          <tbody>
            {checkpoints.map(cp => {
              const totalA = cumA(cp);
              const totalB = cumB(cp);
              const delta = totalA - totalB;
              const isFinal = cp === sharedLaps && sharedLaps === maxLaps;

              return (
                <tr
                  key={cp}
                  style={{
                    borderBottom: "1px solid var(--border)",
                    background: isFinal ? "rgba(59,130,246,0.05)" : undefined,
                    fontWeight: isFinal ? 600 : 400,
                  }}
                >
                  <td style={{ padding: "10px 8px", color: "var(--text2)" }}>
                    Lap {cp}{isFinal ? " (Final)" : ""}
                  </td>
                  <td style={{ padding: "10px 8px", fontVariantNumeric: "tabular-nums" }}>
                    {formatTotalTime(totalA)}
                  </td>
                  <td style={{ padding: "10px 8px", fontVariantNumeric: "tabular-nums" }}>
                    {formatTotalTime(totalB)}
                  </td>
                  <td style={{
                    padding: "10px 8px",
                    fontVariantNumeric: "tabular-nums",
                    color: getDeltaColor(delta),
                    fontWeight: 600,
                  }}>
                    {units.formatDelta(delta / 1000)}
                  </td>
                  <td style={{ padding: "10px 8px", fontSize: "11px" }}>
                    <span style={{
                      color: getDeltaColor(delta),
                      fontWeight: 600,
                    }}>
                      {Math.abs(delta) < 0.01 ? "Even" : delta > 0 ? `${labelB} faster` : `${labelA} faster`}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatTotalTime(seconds: number): string {
  if (!seconds) return "-";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = (seconds % 60).toFixed(3);
  if (hrs > 0) return `${hrs}:${String(mins).padStart(2, "0")}:${secs.padStart(6, "0")}`;
  if (mins > 0) return `${mins}:${secs.padStart(6, "0")}`;
  return `${secs}s`;
}
